import { ApiError, type SessionRefreshOutcome } from './apiClient';
import { refreshTokens } from './cognitoClient';
import { cognitoUsername, computeExpiresAt, readStoredTokens, writeStoredTokens } from './authTokens';
import { isIdleExpired, markIdleSignedOut } from './idleSession';

/**
 * Silent session refresh (POST /api/auth/refresh, Cognito REFRESH_TOKEN_AUTH).
 *
 * Single-flight: while a refresh is running every caller — the 401 handler in
 * apiClient for each concurrent request, and AuthProvider's proactive refresh
 * shortly before expiry — gets the same promise, so one burst of 401s costs
 * one refresh. AuthProvider registers it with apiClient (setSessionRefresher).
 * Outcomes:
 *   'refreshed' new tokens are stored (AuthProvider picks them up),
 *   'invalid'   no refresh token, or it is expired/revoked → sign in again,
 *   'failed'    network / server problem — the session may still be valid.
 * An idle session (no user activity for the idle timeout) is not renewed.
 */
let inFlight: Promise<SessionRefreshOutcome> | null = null;

async function runRefresh(): Promise<SessionRefreshOutcome> {
  const tokens = readStoredTokens();
  if (!tokens || tokens.refreshToken === '') return 'invalid';
  try {
    const result = await refreshTokens(tokens.refreshToken, cognitoUsername(tokens));
    const current = readStoredTokens();
    // Signed out (or another tab stored a newer session) while refreshing:
    // never resurrect or overwrite it.
    if (!current) return 'invalid';
    if (current.refreshToken !== tokens.refreshToken) return 'refreshed';
    writeStoredTokens({
      accessToken: result.accessToken,
      idToken: result.idToken,
      refreshToken: result.refreshToken ?? tokens.refreshToken,
      expiresAt: computeExpiresAt(result.accessToken, result.expiresIn),
    });
    return 'refreshed';
  } catch (err: unknown) {
    if (err instanceof ApiError && (err.status === 400 || err.status === 401)) return 'invalid';
    return 'failed';
  }
}

export function refreshSession(): Promise<SessionRefreshOutcome> {
  // An idle session is never renewed silently (laptop sleep, a hidden tab whose
  // timers were throttled): sign out for inactivity instead (lib/idleSession).
  if (isIdleExpired()) {
    markIdleSignedOut();
    return Promise.resolve('invalid');
  }
  if (!inFlight) {
    inFlight = runRefresh().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}
